import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';

const MESES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

const SeguimientoEntidades = ({ data, isLoading }) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [modo, setModo] = React.useState('dia'); // 'dia' | 'mes' | 'año'

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Agrupa los datos diarios según el modo seleccionado.
  // entidadesDia se suma dentro del periodo; el acumulado toma el último
  // valor del periodo (es una serie acumulativa, monótona creciente).
  const chartData = React.useMemo(() => {
    const diario = [...(data || [])].sort((a, b) => a.fecha.localeCompare(b.fecha));

    if (modo === 'dia') {
      return diario.map(({ fecha, entidadesDia, acumulado }) => {
        const [y, m, d] = fecha.split('-');
        return { key: fecha, label: `${d}/${m}/${y.slice(2)}`, entidadesDia, acumulado };
      });
    }

    const mapa = {};
    diario.forEach(({ fecha, entidadesDia, acumulado }) => {
      const key = modo === 'año' ? fecha.slice(0, 4) : fecha.slice(0, 7);
      if (!mapa[key]) {
        mapa[key] = { entidadesDia: 0, acumulado: 0 };
      }
      mapa[key].entidadesDia += entidadesDia;
      // El último registro del periodo tiene el acumulado más alto
      mapa[key].acumulado = Math.max(mapa[key].acumulado, acumulado);
    });

    return Object.entries(mapa)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, valores]) => {
        let label;
        if (modo === 'año') {
          label = key;
        } else {
          const [y, m] = key.split('-');
          label = `${MESES[Number(m) - 1]} ${y.slice(2)}`;
        }
        return { key, label, entidadesDia: valores.entidadesDia, acumulado: valores.acumulado };
      });
  }, [data, modo]);

  const modos = [
    { id: 'dia', label: 'Día' },
    { id: 'mes', label: 'Mes' },
    { id: 'año', label: 'Año' }
  ];

  const card = {
    background: '#fff',
    border: '1px solid #e2e6ee',
    borderRadius: '12px',
    padding: isMobile ? '16px' : '18px 20px 12px'
  };

  if (isLoading) {
    return (
      <div style={card}>
        <div style={{ width: isMobile ? 200 : 260, height: 18, borderRadius: 5, background: '#eef1f5', animation: 'pulseCard 1.4s ease-in-out infinite' }} />
        <div style={{ width: '100%', height: isMobile ? 200 : 210, marginTop: 18, background: '#f4f6f8', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 44, height: 44, border: '3px solid #e2e8f0', borderTop: '3px solid #0f4f47', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: isMobile ? 12 : 20,
        marginBottom: 14,
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: isMobile ? 0 : 260 }}>
          <div style={{ fontSize: '15.5px', fontWeight: 600, color: '#0f172a' }}>¿Cuántas entidades publican diariamente?</div>
          <div style={{ fontSize: '12.5px', color: '#5b6779', marginTop: 3 }}>Entidades del periodo (barras) y acumulado (línea)</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '12.5px', color: '#5b6779' }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: '#9db8b4' }} />Entidades del periodo
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '12.5px', color: '#5b6779' }}>
            <span style={{ width: 16, height: 2, background: '#0f4f47' }} />Acumulado
          </span>
        </div>
        <div style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 9, background: '#f4f6f8' }}>
          {modos.map((m) => {
            const activo = modo === m.id;
            return (
              <button
                key={m.id}
                className={`dash-seg-btn${activo ? ' dash-seg-btn--activo' : ''}`}
                onClick={() => setModo(m.id)}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  fontWeight: activo ? 600 : 500,
                  color: activo ? '#0f172a' : '#5b6779',
                  background: activo ? '#fff' : 'transparent',
                  borderRadius: 7,
                  padding: '6px 14px',
                  boxShadow: activo ? '0 1px 2px rgba(15,23,42,.12)' : 'none'
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div style={{ height: isMobile ? 200 : 210, position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: isMobile ? 5 : 15, left: isMobile ? -15 : 0, bottom: 5 }}
            >
              <CartesianGrid stroke="#f1f4f8" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#8a94a6' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e6ee' }}
                interval="preserveStartEnd"
                minTickGap={isMobile ? 20 : 30}
              />
              <YAxis
                yAxisId="left"
                allowDecimals={false}
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#8a94a6' }}
                tickLine={false}
                axisLine={false}
                width={isMobile ? 30 : 40}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                allowDecimals={false}
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#8a94a6' }}
                tickLine={false}
                axisLine={false}
                width={isMobile ? 34 : 46}
              />
              <Tooltip
                cursor={{ fill: 'rgba(157,184,180,.16)' }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e6ee',
                  borderRadius: 8,
                  boxShadow: '0 6px 16px rgba(15,23,42,.08)',
                  padding: '8px 12px',
                  fontSize: 12
                }}
                labelStyle={{ color: '#0f172a', fontWeight: 600, marginBottom: 4 }}
                formatter={(value, name) => [value.toLocaleString('es-PE'), name]}
              />
              <Bar
                yAxisId="left"
                dataKey="entidadesDia"
                name="Entidades del periodo"
                fill="#9db8b4"
                radius={[3, 3, 0, 0]}
                maxBarSize={isMobile ? 22 : 34}
                animationDuration={900}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="acumulado"
                name="Acumulado"
                stroke="#0f4f47"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, fill: '#0f4f47', stroke: '#fff', strokeWidth: 2 }}
                animationDuration={900}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ height: isMobile ? 200 : 210, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
          No hay datos de seguimiento de entidades disponibles
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulseCard { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
      `}</style>
    </div>
  );
};

export default SeguimientoEntidades;
